'use client'

import {Card, SegmentedControl, Stack, Text, Title, Badge, Switch } from '@mantine/core';
import {useSyncExternalStore} from 'react';
import {getAudioSettings,getServerAudioSettings,subscribeAudioSettings,setAudioEnabled} from '../lib/services/sound/audio-settings';

import dynamic from 'next/dynamic';

import { useLocale } from '../lib/i18n/use_locale';
import { LOCALES, isLocale, languageNames, writeStoredLocale } from '../lib/i18n/locale';
import { SETTINGS_UI } from '../lib/i18n/strings';
import GamepadSettings from '../components/GamepadSettings';

const WebGpuUnsupportedPage = dynamic(
  () => import('../unsupported/unsupported'),
  { ssr: false, loading: () => <p>Support details...</p> }
);

type Props = {
  revision: string;
  service: string;
};

export default function SettingsPageClient({ revision, service}: Props )
{
  const locale = useLocale();
  const audio=useSyncExternalStore(subscribeAudioSettings,getAudioSettings,getServerAudioSettings);

  return (
    <main
      style={{
        minHeight: '100%',
        background: '#1f222b',
        padding: '24px',
        color: '#ebe6e6',
      }}
    >
      <Stack gap="lg" maw={720}>
        <div>
          <Title order={2} c="#ffffff">
            {SETTINGS_UI.pageTitle[locale]}
          </Title>

        </div>

        <Card
          radius="md"
          padding="lg"
          style={{
            background: '#23262f',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <Stack gap="md">
            <div>
              <Text size="sm" c="#b8b8b8">
                {SETTINGS_UI.currentBuild[locale]}
              </Text>
              <Badge
                mt={8}
                size="lg"
                variant="light"
                color="gray"
                styles={{
                  root: {
                    background: 'rgba(255,255,255,0.06)',
                    color: '#ffffff',
                    textTransform: 'none',
                  },
                }}
              >
                {revision}
              </Badge>
            </div>

            <div>
              <Text size="sm" c="#b8b8b8">
                {SETTINGS_UI.languageLabel[locale]}
              </Text>
              <SegmentedControl
                mt={8}
                size="sm"
                value={locale}
                onChange={(value) => {
                  if (isLocale(value)) writeStoredLocale(value);
                }}
                data={LOCALES.map((l) => ({ value: l, label: languageNames[l] }))}
                aria-label={SETTINGS_UI.languageLabel[locale]}
              />
              <Text size="xs" c="#8a8a8a" mt={6}>
                {SETTINGS_UI.languageDescription[locale]}
              </Text>
            </div>

            <div>
              <Text size="sm" c="#b8b8b8" mb={12}>{SETTINGS_UI.audioLabel[locale]}</Text>
              <Stack gap="md">
                <Switch size="md" color="orange" checked={audio.sfx}
                  onChange={event=>setAudioEnabled("sfx",event.currentTarget.checked)}
                  label={SETTINGS_UI.soundEffects[locale]}
                  description={SETTINGS_UI.soundEffectsDescription[locale]}
                  styles={{label:{color:"#ebe6e6"},description:{color:"#b8b8b8"}}}/>
                <Switch size="md" color="orange" checked={audio.music}
                  onChange={event=>setAudioEnabled("music",event.currentTarget.checked)}
                  label={SETTINGS_UI.music[locale]}
                  description={SETTINGS_UI.musicDescription[locale]}
                  styles={{label:{color:"#ebe6e6"},description:{color:"#b8b8b8"}}}/>
              </Stack>
            </div>
          </Stack>
        </Card>
        <GamepadSettings/>
      </Stack>
      <Stack gap="md">
      <WebGpuUnsupportedPage/>
      </Stack>
    </main>
  );
}
